<template>
  <div ref="resizableArea" class="wrapper rightContent">
    <div
      ref="settingArea"
      v-if="isEditingPoiCategory && !isMapFullScreen"
      v-bind:style="{
        height: `${settingAreaHeight}px`,
        minHeight: `${settingAreaMinHeight}px`,
        maxHeight: `${settingAreaMaxHeight}px`,
      }"
      class="settingArea"
    >
      <EditLocationGroup
        :poiSearchForm.sync="poiSearchForm"
        :mode="mode"
        :locationCount="poiLocationList.length"
        @change="fetchPoiLocations"
      />
    </div>
    <div
      ref="resizer"
      v-if="isEditingPoiCategory && !isMapFullScreen"
      v-bind:style="{ height: `${resizerHeight}px` }"
      @drag="resize"
      @dragend="resizeEnd"
      class="resizer"
      draggable="true"
    />
    <div ref="mapArea" class="map-area">
      <MapComponentWrapper
        ref="mapComponentWrapper"
        v-if="canShowMap || isEditingPoiCategory"
        :has-edit-permission="hasEditPermission"
        :is-map-full-screen="isMapFullScreen"
        :is-editing="isEditingPoiCategory"
        :locationLoading="locationLoading"
        :isSelectedPoiLocationGroup="isSelectedPoiLocationGroup()"
        @open-full-screen="openFullScreenMap"
        @close-full-screen="closeFullScreenMap"
      />
      <EmptyGroupListPlaceholder v-else class="map-placeholder" />
    </div>
    <div
      v-if="isEditingPoiCategory"
      class="map-footer"
      style="text-align: right;"
    >
      <div class="indicator">
        <TargetGroupIndicator />
        <div v-if="notShowPointList" class="red--text">
          地図の範囲内に地点数が100地点以上あるため、表示できません。ズームをして、表示エリアを狭めて下さい。
        </div>
      </div>
      <div>
        <AppButtonCancel @click="cancel">
          保存せず終了
        </AppButtonCancel>
        <AppButtonOk
          v-if="mode.type == 'edit'"
          @click="save"
          :loading="loading"
          :disabled="poiLocationList.length === 0"
          color="primary"
        >
          保存
        </AppButtonOk>
        <AppButtonOk
          v-if="mode.type == 'create'"
          @click="displayLocationGroupNameSetModal()"
          :loading="loading"
          :disabled="poiLocationList.length === 0"
          color="primary"
        >
          保存
        </AppButtonOk>
      </div>
      <v-dialog v-model="isShowModal" persistent max-width="800">
        <v-card>
          <v-card-title>
            <p class="title">作成するグループの名称を入力して下さい</p>
          </v-card-title>
          <v-card-text>
            <v-layout>
              <v-flex xs1>
                <AppForm label="グループ名" />
              </v-flex>
              <v-flex xs9 ml-5 class="target-group-name">
                <v-form ref="form" @submit.prevent>
                  <v-text-field
                    v-if="isShowModal"
                    :rules="[rules.required]"
                    v-model="groupName"
                    autofocus
                  />
                </v-form>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <AppButtonCancel @click="isShowModal = false">
              キャンセル
            </AppButtonCancel>
            <AppButtonOk @click="save" :loading="loading" color="primary">
              保存
            </AppButtonOk>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import MapComponentWrapper from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/Map/MapComponentWrapper'
import EmptyGroupListPlaceholder from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/EmptyGroupListPlaceholder'
import EditLocationGroup from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/LocationGroup/EditLocationGroup'
import { getters, actions } from '@/vuex/modules/pointGroupOperation/constants'
import { POISearchFormModel } from '@/models/POI/POISearchForm'
import { LocationGroupRepository } from '@/models/LocationGroup'
import LocationRepository from '@/models/Location/LocationRepository'
import { AccessibleType, LocationType } from '@/models/POI/Constants'
import TargetGroupIndicator from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/Map/footer/TargetGroupIndicator'
import LocationGroupOperation from '@/api/PointGroup/LocationGroupOperation/LocationGroupOperation'
import { POICategoryRepository } from '@/models/POICategoryRepository'

export default {
  name: 'PointGroupOperationRightContent',
  components: {
    MapComponentWrapper,
    EmptyGroupListPlaceholder,
    EditLocationGroup,
    TargetGroupIndicator,
  },
  props: {
    canShowMap: {
      type: Boolean,
      default: false,
    },
    hasEditPermission: {
      type: Boolean,
      default: false,
    },
    targetGroupType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isMapFullScreen: false,
      resizerHeight: 4, // リサイズ要素の高さ
      settingAreaMinHeight: 30, // 配信対象者設定の高さ最小値
      settingAreaMaxHeight: null,
      settingAreaHeightDefault: null, // 配信対象者設定の高さ初期値
      settingAreaHeight: null,
      lastSettingAreaHeight: null,
      poiSearchForm: null,
      loading: false,
      locationLoading: false,
      isShowModal: false,
      poiLocationList: [],
      groupName: null,
    }
  },
  computed: {
    ownerProfileId() {
      return Number(this.$route.params.ownerProfileId)
    },
    isEditingPoiCategory() {
      return this.$store.getters[getters.isEditingPoiCategory]
    },
    targetGroup() {
      // post/put時のgroupIdで使用
      return this.$store.getters[getters.targetGroup]
    },
    isPoiSearchConditionsEmpty() {
      if (this.poiSearchForm.conditions.length === 0) return true
      return (
        this.poiSearchForm.conditions.length === 1 &&
        this.poiSearchForm.conditions[0].poiSubCategoryId === null
      )
    },
    hasUpdates() {
      return this.$store.getters[getters.targetGroupHasUpdates]
    },
    loadingPointList() {
      return this.$store.getters[getters.loadingPointList]
    },
    notShowPointList() {
      return this.$store.getters[getters.pointListInFrameCount] >= 100
    },
    mode() {
      const id = this.targetGroup.id
      if (id === null)
        return {
          type: 'create',
          text: '登録',
        }
      return {
        type: 'edit',
        text: '編集',
      }
    },
    rules() {
      return LocationGroupOperation.validations
    },
  },
  watch: {
    targetGroup() {
      this.poiSearchForm = POISearchFormModel.assign()
      this.poiLocationList = []
    },
    loadingPointList(value) {
      this.locationLoading = value
    },
  },
  mounted() {
    this.settingAreaHeightDefault = this.$refs.resizableArea.clientHeight / 2
    this.settingAreaMaxHeight = this.$refs.resizableArea.clientHeight - 100
    this.settingAreaHeight = this.settingAreaHeightDefault
  },
  methods: {
    async cancel() {
      const result = await this.$confirm(
        '追加・編集が破棄されますがよろしいですか？',
      )
      if (result) {
        this.$store.dispatch(actions.CLEAR_TARGET_GROUP)
      }
    },
    async save() {
      if (
        (this.poiSearchForm.name === '' || this.poiSearchForm.name === null) &&
        this.mode.type === 'create'
      ) {
        await this.$confirm('エラー', {
          okText: '閉じる',
          onlyOk: true,
          body: 'グループ名を入力してください',
        })
      } else {
        this.poiSearchForm.name = this.groupName
      }
      if (!this.$refs.form.validate()) {
        return
      }
      if (this.poiSearchForm.categoryId === undefined) return
      this.loading = true
      try {
        if (this.mode.type === 'create') await this.saveLocationGroup()
        await LocationRepository.savePoiLocation(
          this.targetGroup.id,
          this.poiSearchForm.categoryId,
          this.isPoiSearchConditionsEmpty ? [] : this.poiSearchForm.conditions,
          this.poiSearchForm.administrativeDistrictType,
          this.removeIdAndNameListFromArea(
            this.poiSearchForm.area,
            this.poiSearchForm.administrativeDistrictType,
          ),
        )
        await this.$store.dispatch(actions.GET_GROUP_LIST, {
          ownerProfileId: this.ownerProfileId,
        })
        this.$store.dispatch(actions.CLEAR_TARGET_GROUP)
        this.isShowModal = false
      } catch (error) {
        if (this.mode.type === 'create')
          await LocationGroupRepository.deleteLocationGroup(this.targetGroup.id)
        this.$confirm('保存に失敗しました', {
          body: `解決しない場合サポートにお問い合わせください。
                  <a href="https://www.geologic.co.jp/inquiry" target="_blank" rel="noopener">https://www.geologic.co.jp/inquiry</a>`,
          okText: '閉じる',
          onlyOk: true,
        })
      } finally {
        this.loading = false
      }
    },
    async saveLocationGroup() {
      const locationGroup = await LocationGroupRepository.addLocationGroup(
        this.ownerProfileId,
        LocationType,
        this.poiSearchForm.name,
        AccessibleType,
      )
      this.targetGroup.id = locationGroup.id
    },
    removeIdAndNameListFromArea(area, administrativeDistrictType) {
      const { prefectureAreaObj, cityAreaObj } = area
      const reformedArea = {
        prefectureAreaObj: {
          countryId: prefectureAreaObj.countryId,
          prefectureIds: prefectureAreaObj.prefectureIds,
        },
        cityAreaObj: {
          countryId: cityAreaObj.countryId,
          prefectures: cityAreaObj.prefectures.map(prefecture => {
            return {
              prefectureId: prefecture.prefectureId,
              cityIds: prefecture.cityIds,
            }
          }),
        },
      }
      return {
        [administrativeDistrictType + 'AreaObj']: reformedArea[
          administrativeDistrictType + 'AreaObj'
        ],
      }
    },
    resize(event) {
      if (event.pageY) {
        this.changeHeight(event.pageY)
      }
    },
    resizeEnd(event) {
      event.preventDefault()
      this.$refs.mapComponentWrapper.resizeMap()
      /**
       * メニュー設定エリアの高さをVueに保存する
       * mim-heightを下回る高さになる場合はmim-heightを保持
       * max-heightを下回る高さになる場合はmax-heightを保持
       */
      const currentSettingAreaHeight = this.settingAreaHeight
      if (currentSettingAreaHeight <= this.settingAreaMinHeight) {
        this.lastSettingAreaHeight = this.settingAreaMinHeight
      } else if (currentSettingAreaHeight >= this.settingAreaMaxHeight) {
        this.lastSettingAreaHeight = this.settingAreaMaxHeight
      } else {
        this.lastSettingAreaHeight = currentSettingAreaHeight
      }
    },
    changeHeight(pageY) {
      const resizableArea = this.$refs.resizableArea
      const settingArea = this.$refs.settingArea
      const mapArea = this.$refs.mapArea

      // 設定エリア上端
      const settingAreaClientRect = settingArea.getBoundingClientRect()
      const settingAreaOffsetTop = settingAreaClientRect.top
      // リサイザーの高さ
      const resizeHeight = this.resizerHeight
      // 設定エリア上端+高さ
      const adjust = settingAreaOffsetTop + resizeHeight
      // 設定エリア高さ設定
      const settingAreaHeight = pageY - adjust
      this.settingAreaHeight = settingAreaHeight

      // 地図エリア高さ設定
      const mapAreaHeight =
        resizableArea.offsetHeight - settingAreaHeight - resizeHeight

      mapArea.style.height = `${mapAreaHeight}px`
    },
    openFullScreenMap() {
      this.isMapFullScreen = true
    },
    closeFullScreenMap() {
      this.isMapFullScreen = false
      this.initializeSettingAreaHeight()
    },
    initializeSettingAreaHeight() {
      const settingAreaMargin = 30
      if (this.lastSettingAreaHeight) {
        this.settingAreaHeight = this.lastSettingAreaHeight
      } else {
        this.settingAreaHeight =
          this.settingAreaHeightDefault + settingAreaMargin
      }
    },
    async fetchPoiLocations() {
      this.locationLoading = true
      this.poiLocationList = await this.poiSearchForm.searchPoiLocationList()
      this.$store.dispatch(actions.SET_CURRENT_POINT_LIST, {
        data: this.poiLocationList,
      })
      this.locationLoading = false
    },
    isSelectedPoiLocationGroup() {
      if (this.targetGroupType === 'poi_location') return true
      return this.isEditingPoiCategory
    },
    async setDefaultLocationGroupName() {
      const poiCategoryList = await POICategoryRepository.getCategory()
      const category = poiCategoryList.filter(
        category => category.id === this.poiSearchForm.categoryId,
      )
      if (category.length === 0) {
        return
      }
      const categoryName = category[0].name
      const areaNames = []
      if (
        this.poiSearchForm.administrativeDistrictType === 'prefecture' &&
        this.poiSearchForm.area.prefectureAreaObj.prefectureIdAndNames.length >
          0
      )
        this.poiSearchForm.area.prefectureAreaObj.prefectureIdAndNames.forEach(
          prefecture => areaNames.push(prefecture.name),
        )
      if (
        this.poiSearchForm.administrativeDistrictType === 'city' &&
        this.poiSearchForm.area.cityAreaObj.prefectures.length > 0
      ) {
        this.poiSearchForm.area.cityAreaObj.prefectures.forEach(prefecture =>
          prefecture.cityIdAndNames.forEach(city => areaNames.push(city.name)),
        )
      }
      if (areaNames.length === 0) {
        this.groupName = categoryName + '_全国'
        return
      }
      this.groupName = categoryName + '_' + areaNames.join('・')
    },
    displayLocationGroupNameSetModal() {
      this.setDefaultLocationGroupName()
      this.isShowModal = true
    },
  },
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
}
.rightContent {
  flex: 1;
  height: 100%;
}
.map-placeholder {
  height: 100%;
}
.settingArea {
  overflow-y: scroll;
  position: relative;
}
.map-area {
  flex: 1;
  min-height: 30px;
}
.resizer {
  background-color: #a0a0a0;
  cursor: row-resize;
}
.map-footer {
  display: flex;
  flex-direction: row;
  padding: 0 20px;
  background: #cce5ff;
}
.indicator {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}
.target-group-name {
  margin-top: -20px;
}
</style>
